import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Eye, Check, Cpu, ArrowRight, Sun, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { toast } from './ui/sonner';

const ProductCard = ({ product, showQuote = false }) => {
  const navigate = useNavigate();
  const { addToCart, loading } = useCart();
  const [selectedColor, setSelectedColor] = useState(product.colors?.[0] || 'black');
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get the current image URL based on selected color, with fallback
  const getCurrentImageUrl = () => {
    if (imageError) {
      return product.image_url;
    }
    return product.images?.[selectedColor] || product.image_url;
  };

  // Reset image error when color changes
  useEffect(() => {
    setImageError(false);
  }, [selectedColor]);

  const isShoppable = product.category === 'basic' || product.category === 'video_basic';
  
  // Check if product is a hub, kit, or camera (needs options selection on detail page)
  const isHubOrKit = product.subcategory === 'hubs' || 
                     product.subcategory === 'kits' || 
                     product.name?.toLowerCase().includes('hub') || 
                     product.name?.toLowerCase().includes('starterkit') || 
                     product.name?.toLowerCase().includes('kit alarme');
  // Caméras : besoin de choisir carte SD + support mural sur la fiche produit
  const isCamera = product.subcategory === 'cameras';

  const canQuickAdd = isShoppable && !isHubOrKit && !isCamera;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isShoppable) return;
    
    setIsAdding(true);
    try {
      const result = await addToCart(product.product_id, 1, selectedColor);
      
      if (result.requiresAuth) {
        // Use React Router navigation with simple state (string path only)
        navigate('/auth', { state: { from: window.location.pathname } });
        return;
      }
      
      if (result.error) {
        toast.error("Erreur lors de l'ajout au panier");
        return;
      }
      
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch (error) {
      console.error('Add to cart error:', error);
      toast.error("Erreur lors de l'ajout au panier");
    } finally {
      setIsAdding(false);
    }
  };

  const hasDiscount = product.original_price && product.price && product.original_price > product.price;
  const discountPercent = hasDiscount 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <div 
      className="group bg-white/[0.02] border border-white/5 hover:border-white/10 rounded-2xl overflow-hidden transition-all duration-300"
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-square bg-[#0a0a0a] overflow-hidden">
        {/* Badges Container - Stacked vertically */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
          {/* Custom Badge (bestseller, new, promo, etc.) */}
          {product.badge && (
            <span className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
              product.badge === 'bestseller' ? 'bg-amber-500/90 text-white' :
              product.badge === 'new' ? 'bg-blue-500/90 text-white' :
              product.badge === 'promo' ? 'bg-red-500/90 text-white' :
              product.badge === 'limited' ? 'bg-purple-500/90 text-white' :
              product.badge === 'exclusive' ? 'bg-fuchsia-500/90 text-white' :
              'bg-white/20 text-white'
            }`}>
              {product.badge === 'bestseller' ? 'Best Seller' :
               product.badge === 'new' ? 'Nouveau' :
               product.badge === 'promo' ? 'Promo' :
               product.badge === 'limited' ? 'Édition Limitée' :
               product.badge === 'exclusive' ? 'Exclusif' :
               product.badge}
            </span>
          )}
          {/* Discount Badge */}
          {hasDiscount && (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold text-white bg-emerald-500/90">
              -{discountPercent}%
            </span>
          )}
          {/* Coming Soon Badge */}
          {product.coming_soon && (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold text-amber-100 bg-amber-500/90 uppercase tracking-wide">
              🔜 Bientôt
            </span>
          )}
          {/* Vision couleur nuit (HL & HLVF) */}
          {(product.name?.includes('HLVF') || (product.name?.includes('HL') && !product.name?.includes('HLVF'))) && (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-purple-500/80 text-white flex items-center gap-1">
              <Sun className="w-3 h-3" />
              Couleur nuit
            </span>
          )}
          {/* Zoom motorisé (HLVF) */}
          {product.name?.includes('HLVF') && (
            <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-gradient-to-r from-amber-500/90 to-orange-500/90 text-white flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Zoom motorisé
            </span>
          )}
        </div>

        {/* AI Badge - Top right */}
        {product.ai_enabled && (
          <div className="absolute top-3 right-3 z-10 px-2 py-1 rounded-full text-[10px] font-semibold text-white bg-blue-500/80 flex items-center gap-1">
            <Cpu className="w-3 h-3" />
            IA
          </div>
        )}

        {/* Product Image - Changes based on selected color */}
        <img 
          src={getCurrentImageUrl()} 
          alt={`${product.name} - ${selectedColor === 'black' ? 'Noir' : 'Blanc'}`}
          className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            if (!imageError) {
              setImageError(true);
              e.target.src = product.image_url || 'https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fhub_256999a1dd%402.png';
            }
          }}
        />

        {/* Quick Actions Overlay */}
        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
          <Link
            to={`/product/${product.product_id}`}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors"
            data-testid={`view-product-${product.product_id}`}
          >
            <Eye className="w-5 h-5" />
          </Link>
          
          {canQuickAdd ? (
            <button
              onClick={handleAddToCart}
              disabled={isAdding || loading}
              className={`w-10 h-10 rounded-full backdrop-blur-sm flex items-center justify-center text-white transition-colors ${
                added 
                  ? 'bg-emerald-500' 
                  : 'bg-white/10 hover:bg-white hover:text-black'
              }`}
              data-testid={`add-to-cart-${product.product_id}`}
            >
              {added ? <Check className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
            </button>
          ) : isShoppable && (
            <Link
              to={`/product/${product.product_id}`}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors"
              data-testid={`configure-product-${product.product_id}`}
            >
              <ArrowRight className="w-5 h-5" />
            </Link>
          )}
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 space-y-3">
        {/* Name */}
        <h3 className="text-base font-medium text-white truncate">
          {product.name}
        </h3>

        {/* Description */}
        <p className="text-white/60 text-sm line-clamp-2 min-h-[40px]">
          {product.description}
        </p>

        {/* Features Tags */}
        {product.features && product.features.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.features.slice(0, 3).map((feature, i) => (
              <span 
                key={i}
                className="px-2 py-0.5 bg-white/5 rounded-full text-[10px] text-white/60"
              >
                {feature}
              </span>
            ))}
          </div>
        )}

        {/* Color Selector - Only for shoppable products */}
        {isShoppable && product.colors && product.colors.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-white/50 uppercase tracking-wider">Couleur:</span>
            <div className="flex gap-2">
              {product.colors.map((color) => (
                <button
                  key={color}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedColor(color);
                  }}
                  className={`w-5 h-5 rounded-full border-2 transition-all ${
                    selectedColor === color 
                      ? 'border-white scale-110' 
                      : 'border-white/20 hover:border-white/40'
                  } ${color === 'black' ? 'bg-zinc-800' : color === 'white' ? 'bg-white' : 'bg-gray-400'}`}
                  data-testid={`color-${color}-${product.product_id}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Price or Quote */}
        <div className="pt-2 space-y-2">
          <div className="flex items-center justify-between">
            {product.price ? (
              <div className="flex items-center gap-2">
                <span className="font-mono text-lg font-semibold text-emerald-400">
                  {product.price.toFixed(2)} €
                </span>
                {hasDiscount && (
                  <span className="font-mono text-xs text-white/50 line-through">
                    {product.original_price.toFixed(2)} €
                  </span>
                )}
              </div>
            ) : (
              <span className="text-emerald-400 font-medium text-sm">Sur devis</span>
            )}

            {/* Add to Cart / Configure / Quote Button */}
            {canQuickAdd ? (
              <Button
                onClick={handleAddToCart}
                disabled={isAdding || loading}
                size="sm"
                className={`rounded-full text-xs px-4 transition-all ${
                added 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-white text-black hover:bg-white/90'
              }`}
              data-testid={`add-btn-${product.product_id}`}
            >
              {added ? (
                <>
                  <Check className="w-3 h-3 mr-1" />
                  Ajouté
                </>
              ) : (
                <>
                  <ShoppingCart className="w-3 h-3 mr-1" />
                  Ajouter
                </>
              )}
            </Button>
          ) : isShoppable ? (
            <Link to={`/product/${product.product_id}`}>
              <Button
                size="sm"
                className="bg-white text-black hover:bg-white/90 rounded-full text-xs px-4"
                data-testid={`configure-btn-${product.product_id}`}
              >
                <ArrowRight className="w-3 h-3 mr-1" />
                Configurer
              </Button>
            </Link>
          ) : showQuote && (
            <Link to={`/${product.category}#quote`}>
              <Button 
                size="sm" 
                variant="outline"
                className="border-white/20 text-white hover:bg-white hover:text-black rounded-full text-xs px-4"
              >
                Devis
              </Button>
            </Link>
          )}
          </div>

          {/* Klarna 3x Payment Option */}
          {product.price && product.price >= 35 && isShoppable && (
            <div className="flex items-center gap-2 pt-1">
              <div className="w-5 h-5 bg-[#FFB3C7] rounded flex items-center justify-center flex-shrink-0">
                <span className="text-black font-black text-[8px]">K.</span>
              </div>
              <span className="text-[11px] text-white/50">
                ou <span className="text-[#FFB3C7] font-medium">{(product.price / 3).toFixed(2)} €</span> × 3 avec Klarna
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
